import { useEffect } from "react";

import { Redirect, Route, Switch, useHistory, useRouteMatch } from "react-router-dom";

import AcceptOrganisationInvite from "@/components/AcceptOrganisationInvite";
import AcceptPartnerLinkInvite from "@/components/AcceptPartnerLinkInvite";
import AuthRoute from "@/components/AuthRoute";
import Error404Page from "@/components/Error404Page";
import Loading from "@/components/Loading";
import Page from "@/components/Page";
import PartnerSignIn from "@/components/PartnerSignIn";
import PartnersMarketingRedirect from "@/components/PartnersMarketingRedirect";
import PartnersPage from "@/components/PartnersPage";
import PaymentSetupRequired from "@/components/PaymentSetupRequired";
import RegenerusCookiePolicyPage from "@/components/RegenerusCookiePolicy";
import RegenerusPrivacyPolicyPage from "@/components/RegenerusPrivacyPolicy";
import RegenerusShippingPolicyPage from "@/components/RegenerusShipping";
import RegenerusTermsAndConditionsPage from "@/components/RegenerusTermsAndConditions";
import RegenerusTermsOfUsePage from "@/components/RegenerusTermsOfUse";
import AddOnErrorModal from "@/components/organisations/checkout/ui/AddOnErrorModal";
import CyrexStoredSampleStockErrorModal from "@/components/organisations/checkout/ui/CyrexStoredSampleStockErrorModal";
import MultipleProductsErrorModal from "@/components/organisations/checkout/ui/MultipleProductsErrorModal";
import ProductNotSoldInLocaleErrorModal from "@/components/organisations/checkout/ui/ProductNotSoldInLocaleErrorModal";
import StockPatientErrorModal from "@/components/organisations/checkout/ui/StockPatientErrorModal";
import OrganisationPatientRegistrationPage from "@/components/organisations/OrganisationPatientRegistrationPage";
import OrganisationPatientRegistrationVerifyEmailPage from "@/components/organisations/OrganisationPatientRegistrationVerifyEmailPage";
import { CMP_PERMISSION_VIEW_RESULTS } from "@/components/organisations/constants";
import PatientViewRoutes from "@/components/patientView";
import ResetPassword from "@/components/resetPassword";
import StudentApplication from "@/components/studentApplication";
import { TestProductModalProvider } from "@/contexts/TestProductModalContext";
import { TestProductOptionProvider } from "@/contexts/TestProductOptionContext";
import { useUserProfileContext } from "@/contexts/UserProfileContext";
import { OrganisationBasketProvider } from "@/contexts/organisations/OrganisationBasketContext";
import { OrganisationProvider } from "@/contexts/organisations/OrganisationContext";
import { PRACTITIONER } from "@/core/constants";
import {
  CMP_DASHBOARD_URL,
  CMP_PRACTITIONER_URL,
  CMP_WITHOUT_ORG,
  PARTNERS_EXTERNAL_URL,
  REGENERUS_COOKIE_POLICY_URL,
  REGENERUS_PRIVACY_URL,
  REGENERUS_SHIPPING_URL,
  REGENERUS_TERMS_AND_CONDITIONS_URL,
  REGENERUS_TERMS_OF_USE_URL,
  getCmpUrl
} from "@/core/urls";
import {
  PRACTITIONER_TEST_PRODUCT_DETAIL_QUERY,
  PRACTITIONER_TEST_PRODUCT_OPTION_FIELDS
} from "@/graphql/shop";
import useOrganisationWithoutUrlParam from "@/hooks/organisations/useOrganisationWithoutUrlParam";

import { ReadOnlyOrderDetailPage } from "../components/orders/OrderDetailPage";
import DashboardSwitcher from "./DashboardSwitcher";
import LoggedOutRoute from "./LoggedOutRoute";
import PartnerRoute from "./PartnerRoute";
import B2CHeader from "./header/B2CHeader";
import CmpHeader from "./header/CmpHeader";
import PartnerDashboard from "./partnerDashboard";
import AffiliateSignupRedirect from "./partnerDashboard/affiliate/SignupRedirect";
import Basket from "./partnerDashboard/practitioner/Basket";
import TestProductModal from "./partnerDashboard/practitioner/TestProductModal";

function RedirectToDashboard() {
  const [organisation, { loading: organisationLoading }] = useOrganisationWithoutUrlParam();
  const { practitionerProfile, loading: profileLoading } = useUserProfileContext();
  const history = useHistory();

  const loading = organisationLoading || profileLoading;

  useEffect(() => {
    if (!loading && !organisation && !practitionerProfile) {
      window.location.href = PARTNERS_EXTERNAL_URL;
    }
    if (!loading && !organisation && practitionerProfile && !practitionerProfile.approved) {
      return history.push(CMP_PRACTITIONER_URL);
    }
  }, [loading, organisation, practitionerProfile, history]);

  if (loading) return <Loading />;

  if (!organisation) return <Loading />;

  const currentUrl = location.pathname;

  const [, rest] = currentUrl.split("/cmp/dashboard");

  const url = getCmpUrl(CMP_DASHBOARD_URL, { organisationId: organisation.hashId });

  return <Redirect to={url + rest + location.search + location.hash} />;
}

function OrganisationRelatedRoutes() {
  const match = useRouteMatch();

  return (
    <OrganisationProvider>
      <OrganisationBasketProvider>
        <TestProductOptionProvider
          testProductOptionFragment={PRACTITIONER_TEST_PRODUCT_OPTION_FIELDS}
          testProductOptionFragmentName="PractitionerTestProductOptionFields"
          testProductOptionFragmentType="TestProductOptionType"
        >
          <TestProductModalProvider
            testProductQuery={PRACTITIONER_TEST_PRODUCT_DETAIL_QUERY}
            testProductFieldName="practitionerTestProduct"
            testProductOptionFragment={PRACTITIONER_TEST_PRODUCT_OPTION_FIELDS}
            testProductOptionFragmentName="practitionerTestProductOption"
            testProductOptionFragmentType="PractitionerTestProductOptionType"
          >
            <Basket />
            <AddOnErrorModal />
            <CyrexStoredSampleStockErrorModal />
            <MultipleProductsErrorModal />
            <StockPatientErrorModal />
            <ProductNotSoldInLocaleErrorModal />
            <Switch>
              <Redirect exact from={match.path} to={`${match.path}/dashboard`} />
              <Route path={`${match.path}/dashboard`}>
                <TestProductModal bg="white" />
                <CmpHeader />
                <PartnerDashboard />
              </Route>
              <PartnerRoute
                path={`${match.path}/patients/:patientId`}
                permissionsRequired={[CMP_PERMISSION_VIEW_RESULTS]}
              >
                {/* We need another TestProductModal here since we want access to patientId in the params */}
                <TestProductModal bg="white" />
                <CmpHeader hideNav />
                <PatientViewRoutes />
              </PartnerRoute>
            </Switch>
          </TestProductModalProvider>
        </TestProductOptionProvider>
      </OrganisationBasketProvider>
    </OrganisationProvider>
  );
}

function Partners() {
  const match = useRouteMatch();

  return (
    <Switch>
      <Route exact path={match.path}>
        <PartnersMarketingRedirect />
      </Route>
      <Route path={`${match.path}/affiliate`} exact>
        <B2CHeader />
        <AffiliateSignupRedirect />
      </Route>
      <Route path={`${match.path}/practitioner`} exact>
        <B2CHeader />
        <PartnersPage tier={PRACTITIONER} />
      </Route>
      <Route path={`${match.path}/student_application`} exact>
        <B2CHeader />
        <StudentApplication />
      </Route>
      <Redirect from={`${match.path}/signin`} to={`${match.path}/login`} exact />
      <LoggedOutRoute path={`${match.path}/login`} exact partners>
        <CmpHeader />
        <PartnerSignIn />
      </LoggedOutRoute>
      <LoggedOutRoute path={`${match.path}/password-reset`} partners>
        <CmpHeader />
        <ResetPassword partners />
      </LoggedOutRoute>
      <Route
        path={`${match.path}/patient_pay_order_confirmation/:id`}
        component={ReadOnlyOrderDetailPage}
        exact
      />
      <Route path={`${match.path}/payment-setup-required`} component={PaymentSetupRequired} exact />
      <AuthRoute
        path={`${match.path}/invite/:inviteId/accept/:token`}
        component={AcceptOrganisationInvite}
        exact
      />
      <Route
        path={`${match.path}/patient_invite/organisation/:organisation/partner/:partnerLinkCode`}
        component={AcceptPartnerLinkInvite}
        exact
      />
      <AuthRoute
        renderLoadingTemplate={loadingContent => (
          <Page addNavBarHeight={false} isPartnerPage>
            <DashboardSwitcher bg="partners" isPartnerPage />
            {loadingContent}
          </Page>
        )}
        path={`${match.path}/dashboard`}
        component={RedirectToDashboard}
      />

      <LoggedOutRoute
        path={`${match.path}/organisations/:organisationId/patient-registration/verify-email`}
        exact
        partners
      >
        <B2CHeader />
        <OrganisationPatientRegistrationVerifyEmailPage />
      </LoggedOutRoute>

      <LoggedOutRoute
        path={`${match.path}/organisations/:organisationId/patient-registration/:token`}
        exact
        partners
      >
        <B2CHeader />
        <OrganisationPatientRegistrationPage />
      </LoggedOutRoute>

      <AuthRoute
        renderLoadingTemplate={loadingContent => (
          <Page addNavBarHeight={false} isPartnerPage>
            <DashboardSwitcher bg="partners" isPartnerPage />
            {loadingContent}
          </Page>
        )}
        path={`${match.path}/organisations/:organisationId`}
      >
        <OrganisationRelatedRoutes />
      </AuthRoute>

      <Route path={`${match.path}/:tab`} exact>
        <Redirect to={CMP_WITHOUT_ORG} />
      </Route>
      <Redirect
        from={`${match.path}/test-result/:clientId/:userResultId`}
        to={CMP_WITHOUT_ORG}
        exact
      />
      <Redirect
        from={`${match.path}/test-biomarker/:clientId/:userDatapointId`}
        to={CMP_WITHOUT_ORG}
        exact
      />
      <Redirect from={`${match.path}/:clientId/recommendations`} to={CMP_WITHOUT_ORG} exact />
      <Redirect
        from={`${match.path}/:clientId/recommendations/:recommendationId`}
        to={CMP_WITHOUT_ORG}
        exact
      />
      <Redirect from={`${match.path}/supplements/:clientId/:slug`} to={CMP_WITHOUT_ORG} />
      <Redirect from={`${match.path}/food-list/:clientId/`} to={CMP_WITHOUT_ORG} />
      <Error404Page />
    </Switch>
  );
}

export default Partners;
